import { useCallback, useMemo } from 'react'
import { useBuildingStore } from '../../stores/buildingStore'
import { useViewStore } from '../../stores/viewStore'
import { useRealtimeStore } from '../../stores/realtimeStore'
import { useUIStore } from '../../stores/uiStore'
import { useCanAccess } from '../../hooks/useCanAccess'
import FloorList from '../floor/FloorList'
import DisplayToggles from '../view/DisplayToggles'
import ExplodeSlider from '../view/ExplodeSlider'
import Toggle3D2D from '../view/Toggle3D2D'
import ViewSelector from '../view/ViewSelector'
import ModeToggle from '../view/ModeToggle'
import IFCRotationSlider from '../view/IFCRotationSlider'
import PersonnelPanel from '../occupant/PersonnelPanel'
import LegendPanel from '../common/LegendPanel'
import StatsPanel from '../common/StatsPanel'
import RoutingPanel from '../routing/RoutingPanel'
import ZoneEditor from '../zone/ZoneEditor'
import WallEditor from '../wall/WallEditor'
import PathEditor from '../path/PathEditor'
import LuxNavixFilters from '../common/LuxNavixFilters'
import '../../styles/panels.css'

const TABS = [
  { id: 'floors', label: 'Etages' },
  { id: 'personnel', label: 'Personnel' },
  { id: 'routing', label: 'Itineraire' },
  { id: 'luxnavix', label: 'LuxNavix' },
  { id: 'zones', label: 'Zones' },
  { id: 'murs', label: 'Murs' },
  { id: 'chemins', label: 'Chemins' },
  { id: 'stats', label: 'Stats' },
  { id: 'legend', label: 'Legende' },
] as const

export default function SidePanel() {
  const currentSiteId = useBuildingStore((s) => s.currentSiteId)
  const loading = useBuildingStore((s) => s.loading)
  const activeTab = useUIStore((s) => s.activeTab)
  const setActiveTab = useUIStore((s) => s.setActiveTab)
  const routesMode = useViewStore((s) => s.routesMode)
  const focusCameraFn = useViewStore((s) => s.focusCameraFn)
  const setCurrentRoute = useViewStore((s) => s.setCurrentRoute)
  const clearRoute = useViewStore((s) => s.clearRoute)

  // Read permissions
  const canReadZones = useCanAccess('locations', 'read')
  const canReadWalls = useCanAccess('walls', 'read')
  const canReadPaths = useCanAccess('paths', 'read')

  // Write permissions (must call hooks unconditionally)
  const canCreateZones = useCanAccess('locations', 'create')
  const canUpdateZones = useCanAccess('locations', 'update')
  const canEditZones = canCreateZones || canUpdateZones
  const canCreateWalls = useCanAccess('walls', 'create')
  const canUpdateWalls = useCanAccess('walls', 'update')
  const canEditWalls = canCreateWalls || canUpdateWalls

  // Admin
  const canUpdateSites = useCanAccess('sites', 'update')

  const visibleTabs = useMemo(() =>
    TABS.filter((tab) => {
      if (tab.id === 'zones' && !canReadZones) return false
      if (tab.id === 'murs' && !canReadWalls) return false
      if (tab.id === 'chemins' && !canReadPaths) return false
      if (tab.id === 'routing' && !canReadPaths) return false
      return true
    }),
    [canReadZones, canReadWalls, canReadPaths],
  )

  // Phase 1: Focus camera on badge
  const handleFocusBadge = useCallback((badgeCode: string) => {
    if (!focusCameraFn) return
    const positions = useRealtimeStore.getState().positions
    const realtimePos = positions.get(badgeCode)
    if (realtimePos) {
      const locations = useBuildingStore.getState().locations
      const zone = locations.find((l) => l.name === realtimePos.locationName)
      if (zone) {
        focusCameraFn(zone.x, 0.3, zone.z)
        return
      }
    }
    const badge = useBuildingStore.getState().badges.find((b) => b.badge_code === badgeCode)
    if (badge) {
      focusCameraFn(0, 0.3, 0)
    }
  }, [focusCameraFn])

  // Phase 3: Route callbacks
  const handleRouteCalculated = useCallback((path: { x: number; z: number; floor: number }[]) => {
    setCurrentRoute(path)
  }, [setCurrentRoute])

  const handleRouteClear = useCallback(() => {
    clearRoute()
  }, [clearRoute])

  return (
    <aside className="side-panel">
      <div className="side-panel__header">Dashboard</div>
      <div className="side-panel__content">
        {!currentSiteId && (
          <p className="side-panel__empty">Selectionnez un site</p>
        )}
        {loading && <p className="side-panel__loading">Chargement...</p>}
        {currentSiteId && !loading && (
          <>
            <div className="side-panel__tabs">
              {visibleTabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`side-panel__tab ${activeTab === tab.id ? 'side-panel__tab--active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'floors' && (
              <>
                <FloorList />
                <div className="panel-section__row">
                  <ExplodeSlider />
                  <Toggle3D2D />
                </div>
                <DisplayToggles />
                <ViewSelector />
                <ModeToggle />
                {canUpdateSites && <IFCRotationSlider />}
              </>
            )}

            {activeTab === 'personnel' && (
              <PersonnelPanel onFocusBadge={handleFocusBadge} />
            )}

            {activeTab === 'routing' && (
              <RoutingPanel
                onRouteCalculated={handleRouteCalculated}
                onRouteClear={handleRouteClear}
              />
            )}

            {activeTab === 'luxnavix' && <LuxNavixFilters />}

            {activeTab === 'zones' && (
              canEditZones ? (
                <ZoneEditor />
              ) : (
                <div className="side-panel__readonly">
                  <div className="panel-section__title">Zones (lecture seule)</div>
                  <p className="side-panel__hint">
                    Vous n&apos;avez pas les droits d&apos;edition des zones.
                  </p>
                </div>
              )
            )}

            {activeTab === 'murs' && (
              canEditWalls ? (
                <WallEditor />
              ) : (
                <div className="side-panel__readonly">
                  <div className="panel-section__title">Murs (lecture seule)</div>
                  <p className="side-panel__hint">
                    Vous n&apos;avez pas les droits d&apos;edition des murs.
                  </p>
                </div>
              )
            )}

            {activeTab === 'chemins' && <PathEditor />}

            {activeTab === 'stats' && <StatsPanel />}

            {activeTab === 'legend' && <LegendPanel />}

            {routesMode && activeTab !== 'routing' && (
              <div className="side-panel__hint">
                Mode itineraire actif - onglet Itineraire
              </div>
            )}
          </>
        )}
      </div>
    </aside>
  )
}
